import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable()
export class SharedService {
  apiURL: string = environment.apiURL;

  sendEventSubject = new BehaviorSubject<any>(null);

  constructor(private http: HttpClient) {
  }

  getEventBehaviorSubject(): any {
    return this.sendEventSubject;
  }

  /**
   * Upload file
   * @param formData
   */
  uploadFile(formData): Observable<any> {
    return this.http.post(`${this.apiURL}/upload`, formData);
  }

  /**
   * Upload clients from excel file
   */
  uploadClients(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.apiURL}/clients/upload`, formData);
  }

  /**
   * Get clients
   */
  getAllClients(name?: string): Observable<any> {
    let params = new HttpParams();

    if (name) params = params.append('search', name);
    return this.http.get(`${this.apiURL}/clients`, { params });
  }

  /**
   * Get materials
   */
  getAllMaterials(name?: string, workRequestId?: string): Observable<any> {
    let params = new HttpParams();

    if (name) params = params.append('search', name);
    if (workRequestId) params = params.append('workRequestId', workRequestId);

    return this.http.get(`${this.apiURL}/materials`, { params });
  }

  /**
   * Get orders
   */
  getOrders(skip: number, limit: number, search?: string): Observable<any> {
    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (search) params = params.append('search', search);

    return this.http.get(`${this.apiURL}/work-orders/import`, { params });
  }

  /**
   * Get production orders
   */
  getProductionOrders(search?: string): Observable<any> {
    let params = new HttpParams();

    if (search) params = params.append('search', search);

    return this.http.get(`${this.apiURL}/work-orders/production`, { params });
  }

  /**
   * Get active orders
   */
  getActiveOrders(includeOperations: string, sector: string, search?: string): Observable<any> {
    let params = new HttpParams()
      .set('includeOperations', includeOperations)
      .set('sector', sector);
    if (search) params = params.append('search', search);

    return this.http.get(`${this.apiURL}/work-orders/active`, { params });
  }

  /**
   * Get sectors
   */
  getSectors(): Observable<any> {
    return this.http.get(`${this.apiURL}/enums?enumType=sectors`);
  }

  /**
   * Get offer statuses
   */
  getOfferStatuses(): Observable<any> {
    return this.http.get(`${this.apiURL}/enums?enumType=offerStatus`);
  }

  /**
   * Get offer rejection reasons
   */
  getOfferRejectionReasons(): Observable<any> {
    return this.http.get(`${this.apiURL}/enums?enumType=offerRejectionReasons`);
  }

  /**
   * Get machines
   */
  getMachines(sector: string, locationId?: string, unique?: boolean): Observable<any> {
    let params = new HttpParams()
      .set('sector', sector);
    if (locationId) params = params.append('locationId', locationId);
    if (unique) params = params.append('unique', unique);
    return this.http.get(`${this.apiURL}/machines`, { params });
  }

  /**
   * Get employee types
   */
  getEmployeeTypes(): Observable<any> {
    return this.http.get(`${this.apiURL}/enums?enumType=employeeTypes`);
  }

  /**
   * Get sectors from localStorage
   */
  getSectorList(): any {
    return JSON.parse(localStorage.getItem('Sectors'));
  }

  /**
   * Get offer statuses from localStorage
   */
  getOfferStatusesList(): any {
    return JSON.parse(localStorage.getItem('OfferStatuses'));
  }

  /**
   * Get offer rejection reasons from localStorage
   */
  getOfferRejectionReasonsList(): any {
    return JSON.parse(localStorage.getItem('OfferRejectionReasons'));
  }

  /**
   * Get order materials
   */
  getOrderMaterials(orderId: string, status?: string): Observable<any> {
    let params = new HttpParams();
    if (status) params = params.append('status', status);

    return this.http.get(`${this.apiURL}/work-orders/${orderId}/materials`, { params });
  }

  /**
   * Translate status
   */
  translateStatus(status: string): string {
    const statuses = [
      { title: 'Planirano', key: 'Planned' },
      { title: 'Dodatni materijal', key: 'Additional' },
      { title: 'Šteta zaposleni', key: 'DamageByEmployee' },
      { title: 'Šteta proizvodnja', key: 'DamageByMachine' },
      { title: 'Šteta dobavljač', key: 'DamageByVendor' },
    ];

    const index = statuses.findIndex(item => item.key === status);

    return statuses[index].title;
  }

  /**
   * Get exchange rate
   */
  getExchangeRate(): Observable<any> {
    return this.http.get(`${this.apiURL}/currency-converter`);
  }

  /**
   * Get all operations
   */
  getAllOperations(search: string, sector: string): Observable<any> {
    let params = new HttpParams();

    if (search) params = params.append('search', search);
    if (sector) params = params.append('sector', sector);

    return this.http.get(`${this.apiURL}/operations`, { params });
  }

  /**
   * Get all clients for set income estimation
   * @param search
   */
  getClientsForIncomeEstimation(search: string): Observable<any> {
    let params = new HttpParams();

    if (search) params = params.append('search', search);
      
    return this.http.get(`${this.apiURL}/clients/monthly-plan/all`, {params});
  }

  /**
   * Get locations from API
   */
  getLocations(): Observable<any> {
    return this.http.get(`${this.apiURL}/locations`);
  }

  /**
   * Get locations from localStorage
   */
  getLocationList(): any {
    return JSON.parse(localStorage.getItem('Locations'));
  }
}
